const CART_DRAWER_CLOSE_TIMEOUT = 800;

class CollectionWishlistDrawer extends HTMLElement {
  connectedCallback() {
    this.sheet = this.querySelector('sheet-dialog');
    this.optionsContainer = this.querySelector('[data-wishlist-options]');
    this.error = this.querySelector('[data-wishlist-error]');
    this.submit = this.querySelector('[data-wishlist-submit]');
    this.submitIcon = this.querySelector('[data-wishlist-submit-icon]');
    this.submitLabel = this.querySelector('[data-wishlist-submit-label]');
    this.optionsTemplate = this.querySelector('[data-wishlist-options-template]');
    this.optionTemplate = this.querySelector('[data-wishlist-option-template]');
    this.sheet.addEventListener('sheet:close', () => {
      this.cancelPendingTransition();
      this.restoreCartDrawer();
    });
    this.submit.addEventListener('click', () => this.commitChanges());

    document.addEventListener('click', (event) => this.handleWishlistButtonClick(event), true);
    document.addEventListener('pointerdown', (event) => {
      const productUrl = event.target.closest('[data-collection-wishlist-button]')?.dataset.productUrl;
      if (productUrl) this.fetchProductData(productUrl);
    });
    this.productDataCache = new Map();

    this.prefetchOnHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    this.hoverPrefetchButtons = new WeakSet();
    this.wishlistProductIds = new Set();
    this.wishlistVariantIds = new Set();
    window.SwymCallbacks = window.SwymCallbacks || [];
    window.SwymCallbacks.push((swat) => this.loadWishlistState(swat));
    if (window._swat) this.loadWishlistState(window._swat);
    window.addEventListener('wishlist:updated', (event) => {
      if (event.detail?.source === this) return;
      if (window._swat) this.loadWishlistState(window._swat);
    });

    this.cardObserver = new MutationObserver((mutations) => {
      const addsWishlistButton = (node) => node.nodeType === 1
        && (node.matches?.('[data-collection-wishlist-button]') || node.querySelector?.('[data-collection-wishlist-button]'));
      const relevant = mutations.some((mutation) => Array.from(mutation.addedNodes).some(addsWishlistButton));
      if (relevant) this.scheduleCardWishlistStates();
    });
    this.cardObserver.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('product-cards:hydrated', () => this.scheduleCardWishlistStates());
    this.transitionToken = 0;
  }

  async handleWishlistButtonClick(event) {
    const button = event.target.closest('[data-collection-wishlist-button]');
    if (!button || button.dataset.loading) return;
    event.preventDefault();
    event.stopPropagation();
    const productUrl = button.dataset.productUrl;
    if (!productUrl) return;
    button.dataset.loading = 'true';
    let data;
    let closedCartDrawer;
    try {
      const closingCartDrawer = this.closeEnclosingCartDrawer(button);
      data = await this.fetchProductData(productUrl);
      closedCartDrawer = await closingCartDrawer;
    } finally {
      delete button.dataset.loading;
    }
    if (!data) {
      // The cart already slid away during the fetch — bring it back so the tap doesn't leave nothing on screen.
      closedCartDrawer?.show();
      return;
    }
    this.product = data;
    this.sizeDisplayStrategyHandle = data.size_display_strategy_handle || '';
    // Not stacked over the cart: it sits at the max z-index, traps focus in #slideCart and
    // handles Escape itself. Instead it's hidden while this panel is up and shown again on close.
    this.cartDrawerToRestore = closedCartDrawer;
    const trigger = closedCartDrawer ? document.querySelector('drawer-show[drawer="cart-drawer"] .btn-cart') : button;
    this.open(data, trigger);
  }

  async closeEnclosingCartDrawer(button) {
    const cartDrawer = button.closest('cart-drawer');
    if (!cartDrawer?.open) return null;
    // hide() releases the scroll lock and moves focus, forcing a style recalc — let the tap paint first.
    await window.waitForPaint();
    cartDrawer.hide();
    await transitionEnd(cartDrawer.querySelector('.drawer'), 'transform', CART_DRAWER_CLOSE_TIMEOUT);
    return cartDrawer;
  }

  // Sends a shopper who opened this from a cart upsell back to their cart, however the panel closed.
  restoreCartDrawer() {
    const cartDrawer = this.cartDrawerToRestore;
    this.cartDrawerToRestore = null;
    if (cartDrawer && !cartDrawer.open) cartDrawer.show();
  }

  fetchProductData(productUrl, { priority } = {}) {
    if (this.productDataCache.has(productUrl)) return this.productDataCache.get(productUrl);
    const promise = this.requestProductData(productUrl, priority);
    this.productDataCache.set(productUrl, promise);
    return promise;
  }

  async requestProductData(productUrl, priority) {
    try {
      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('view', 'wishlist-data');
      const requestPath = url.pathname + url.search;
      const response = await fetch(requestPath, priority ? { priority } : undefined);
      if (!response.ok) throw new Error(`Unable to load product data (${response.status} for ${requestPath})`);
      const body = await response.text();
      if (body.trimStart().startsWith('<')) {
        throw new Error(`${requestPath} returned HTML, not JSON — check templates/product.wishlist-data.liquid is on this theme`);
      }
      const data = JSON.parse(body);
      data.url = productUrl;
      return data;
    } catch (exception) {
      console.error(exception);
      this.productDataCache.delete(productUrl);
      return null;
    }
  }

  loadWishlistState(swat, { notify = false, onComplete } = {}) {
    swat.fetch((products) => {
      this.wishlistProductIds = new Set((products || []).map((product) => String(product.empi)));
      this.wishlistVariantIds = new Set((products || []).map((product) => String(product.epi)));
      this.updateCardWishlistStates();
      this.updateVariantToggleStates();
      if (notify) {
        window.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { source: this } }));
        this.updateHeaderWishlistCount((products || []).length);
      }
      onComplete?.();
    });
  }

  updateHeaderWishlistCount(count) {
    document.querySelectorAll('.wishlist-count').forEach((element) => {
      element.dataset.count = String(count);
      const ariaLiveElement = element.querySelector('[aria-live]');
      if (ariaLiveElement) ariaLiveElement.textContent = String(count);
    });
  }

  scheduleCardWishlistStates() {
    if (this.pendingCardStateFrame) return;
    this.pendingCardStateFrame = requestAnimationFrame(() => {
      this.pendingCardStateFrame = null;
      this.updateCardWishlistStates();
    });
  }

  updateCardWishlistStates() {
    document.querySelectorAll('[data-collection-wishlist-button][data-product-id]').forEach((button) => {
      const isWishlisted = this.wishlistProductIds.has(String(button.dataset.productId));
      button.classList.toggle('swym-added', isWishlisted);
      button.setAttribute('aria-pressed', String(isWishlisted));
      if (this.prefetchOnHover) this.attachHoverPrefetch(button);
    });
  }

  attachHoverPrefetch(button) {
    if (this.hoverPrefetchButtons.has(button)) return;
    this.hoverPrefetchButtons.add(button);
    let hoverTimeoutId = null;
    button.addEventListener('pointerenter', () => {
      const productUrl = button.dataset.productUrl;
      if (!productUrl || this.productDataCache.has(productUrl)) return;
      hoverTimeoutId = setTimeout(() => this.fetchProductData(productUrl, { priority: 'low' }), 150);
    });
    button.addEventListener('pointerleave', () => clearTimeout(hoverTimeoutId));
  }

  open(product, trigger) {
    this.cancelPendingTransition();
    this.product = product;
    this.trigger = trigger;
    const productTitle = product.title.split(' - ');
    const productVariantIds = new Set(product.variants.map((variant) => String(variant.id)));
    this.initialVariantIds = new Set([...this.wishlistVariantIds].filter((id) => productVariantIds.has(id)));
    this.hasSizeChoice = this.productHasSizeChoice();
    this.pendingVariantId = this.hasSizeChoice
      ? (this.initialVariantIds.values().next().value || null)
      : String(this.selectedOrFirstAvailableVariant(product).id);
    this.querySelector('[data-wishlist-product-title]').textContent = productTitle[0];
    this.querySelector('[data-wishlist-variant-title]').textContent = productTitle[1] || '';
    const image = product.featured_image || product.images?.[0]?.src || product.images?.[0];
    this.querySelector('[data-wishlist-image]').src = typeof image === 'string' ? image : image?.src || '';
    this.querySelector('[data-wishlist-image]').alt = product.title;
    const initialVariant = product.variants.find((variant) => String(variant.id) === this.pendingVariantId)
      || this.selectedOrFirstAvailableVariant(product);
    this.querySelector('[data-wishlist-variant-price]').textContent = this.formatVariantPrice(initialVariant.price);
    this.updateProductLinks(initialVariant.id);
    this.resetSubmitState();
    this.renderOptions();
    this.sheet.open(trigger);
  }

  visibleOptionIndexes() {
    const colourOptionName = (this.dataset.colourOptionName || '').trim().toLowerCase();
    return productOptionNames(this.product)
      .map((name, index) => ({ name, index }))
      .filter(({ name }) => name.trim().toLowerCase() !== colourOptionName)
      .map(({ index }) => index);
  }

  productHasSizeChoice() {
    if (this.product.variants.length <= 1) return false;
    const indexes = this.visibleOptionIndexes();
    if (indexes.length === 0) return false;
    const variantKey = (variant) => indexes.map((index) => variant.options[index]).join('␟');
    return new Set(this.product.variants.map(variantKey)).size > 1;
  }

  selectedOrFirstAvailableVariant(product) {
    return product.variants.find((variant) => variant.available) || product.variants[0];
  }

  variantSizeLabel(variant) {
    return this.visibleOptionIndexes()
      .map((index) => this.formatSize(variant.options[index]))
      .join(' / ');
  }

  formatSize(size) {
    return size ? window.formatVariantSize(size, this.sizeDisplayStrategyHandle) : size;
  }

  productUrl(variantId) {
    const base = this.product.url || '';
    if (!variantId) return base;
    return `${base}${base.includes('?') ? '&' : '?'}variant=${variantId}`;
  }

  updateProductLinks(variantId) {
    const url = this.productUrl(variantId);
    const titleLink = this.querySelector('[data-wishlist-product-title]');
    const imageLink = this.querySelector('[data-wishlist-image-link]');
    if (titleLink) titleLink.href = url;
    if (imageLink) imageLink.href = url;
  }

  renderOptions() {
    if (!this.hasSizeChoice) {
      this.optionsContainer.replaceChildren();
      this.updateVariantToggleStates();
      return;
    }
    const optionNames = productOptionNames(this.product);
    const visibleOptionIndexes = this.visibleOptionIndexes();
    const fieldset = this.optionsTemplate.content.firstElementChild.cloneNode(true);
    const legend = visibleOptionIndexes.map((index) => optionNames[index]).join(' / ');
    fieldset.querySelector('[data-wishlist-options-prompt]').textContent = `${this.dataset.selectALabel} ${legend.toLowerCase()}`;
    fieldset.querySelector('[data-wishlist-options-name]').textContent = legend;
    const grid = fieldset.querySelector('[data-wishlist-options-grid]');
    this.product.variants.forEach((variant) => {
      const label = this.variantSizeLabel(variant);
      const button = this.optionTemplate.content.firstElementChild.cloneNode(true);
      button.dataset.variantId = variant.id;
      button.querySelector('[data-wishlist-option-label]').textContent = label;
      button.querySelector('.wishlist-stock-dot').dataset.available = variant.available;
      button.addEventListener('click', () => this.toggleVariant(variant, button));
      grid.appendChild(button);
    });
    this.optionsContainer.replaceChildren(fieldset);
    this.updateVariantToggleStates();
  }

  updateVariantToggleStates() {
    this.optionsContainer?.querySelectorAll('[data-variant-id]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.variantId === this.pendingVariantId));
    });
    if (this.submit) this.submit.disabled = !this.canSubmit();
    this.updateSubmitLabel();
    this.updateOptionsStatus();
  }

  // Which action clicking submit would actually perform, so the button never claims
  // to "add" when a deselected size means it would remove the item instead.
  pendingActionType() {
    const initialIds = this.initialVariantIds || new Set();
    const isAdding = this.pendingVariantId !== null && !initialIds.has(this.pendingVariantId);
    const isRemoving = [...initialIds].some((id) => id !== this.pendingVariantId);
    if (isAdding && isRemoving) return 'update';
    if (isRemoving) return 'remove';
    if (isAdding) return 'add';
    // No pending changes: a selected variant here is already wishlisted (that's how it got
    // selected on open), so the disabled button should read "remove", not "add".
    return this.pendingVariantId !== null ? 'remove' : 'add';
  }

  updateSubmitLabel() {
    if (!this.submit || this.submit.dataset.state === 'success') return;
    const action = this.pendingActionType();
    this.submitLabel.textContent = action === 'remove' ? this.submit.dataset.labelRemove
      : action === 'update' ? this.submit.dataset.labelUpdate
      : this.submit.dataset.labelDefault;
  }

  updateOptionsStatus() {
    const prompt = this.optionsContainer?.querySelector('[data-wishlist-options-prompt]');
    const name = this.optionsContainer?.querySelector('[data-wishlist-options-name]');
    const selected = this.optionsContainer?.querySelector('[data-wishlist-options-selected]');
    const stock = this.optionsContainer?.querySelector('[data-wishlist-options-stock]');
    if (!prompt || !name || !selected || !stock) return;
    const variant = this.pendingVariantId
      ? this.product.variants.find((variant) => String(variant.id) === this.pendingVariantId)
      : null;
    prompt.classList.toggle('hidden', !!variant);
    name.classList.toggle('hidden', !variant);
    selected.classList.toggle('hidden', !variant);
    stock.classList.toggle('hidden', !variant);
    if (!variant) return;
    selected.textContent = this.variantSizeLabel(variant);
    stock.textContent = variant.available ? this.dataset.inStockLabel : this.dataset.soldOutLabel;
    stock.classList.toggle('text-[#008700]', variant.available);
    stock.classList.toggle('text-[#FF0000]', !variant.available);
  }

  toggleVariant(variant, button) {
    this.error.textContent = '';
    this.querySelector('[data-wishlist-variant-price]').textContent = this.formatVariantPrice(variant.price);
    this.updateProductLinks(variant.id);
    const id = String(variant.id);
    this.pendingVariantId = this.pendingVariantId === id ? null : id;
    this.updateVariantToggleStates();
  }

  formatVariantPrice(cents) {
    const format = Number(cents) % 100 === 0
      ? window.Shopify.money_format.replace('amount', 'amount_no_decimals')
      : window.Shopify.money_format;
    return window.Shopify.formatMoney(cents, format);
  }

  resetSubmitState() {
    this.submit.dataset.state = 'default';
    this.submitIcon.dataset.state = 'default';
  }

  showSuccessState(action) {
    this.submit.dataset.state = 'success';
    this.submitIcon.dataset.state = 'success';
    this.submitLabel.textContent = action === 'remove' ? this.submit.dataset.labelRemoved : this.submit.dataset.labelSuccess;
  }

  canSubmit() {
    if (!this.initialVariantIds) return false;
    return this.pendingVariantId !== null || this.initialVariantIds.size > 0;
  }

  async commitChanges() {
    if (!window._swat || !this.canSubmit()) return;
    const token = ++this.transitionToken;
    const action = this.pendingActionType();
    this.submit.disabled = true;
    this.error.textContent = '';
    const pendingVariant = this.pendingVariantId
      ? this.product.variants.find((variant) => String(variant.id) === this.pendingVariantId)
      : null;
    const variantsToAdd = (action === 'add' || action === 'update') && pendingVariant ? [pendingVariant] : [];
    const variantsToRemove = action === 'add'
      ? []
      : this.product.variants.filter((variant) => this.initialVariantIds.has(String(variant.id)));
    try {
      const addedVariantsInCartPromise = Promise.all(
        variantsToAdd.map((variant) => window.isVariantInCart(variant.id)),
      );
      await Promise.all([
        ...variantsToAdd.map((variant) => new Promise((resolve, reject) =>
          window._swat.addToWishList(this.variantPayload(variant), resolve, reject),
        )),
        ...variantsToRemove.map((variant) => new Promise((resolve, reject) =>
          window._swat.api.removeFromWishList(this.variantPayload(variant), resolve, reject),
        )),
      ]);
      const anyAddedVariantInCart = (await addedVariantsInCartPromise).some(Boolean);
      if (anyAddedVariantInCart) {
        window._swat.ui.showSuccessNotification({ message: this.dataset.alreadyInCartLabel });
      }
      this.loadWishlistState(window._swat, {
        notify: true,
        onComplete: () => {
          // Bail if the panel was closed (or reopened for another product) while this
          // request was in flight — closing at any point cancels the whole sequence.
          if (token !== this.transitionToken) return;
          this.showSuccessState(action);
          this.autoOpenTimeoutId = setTimeout(() => {
            this.autoOpenTimeoutId = null;
            if (token !== this.transitionToken) return;
            // Hand the cart over so it comes back when the summary closes, not underneath it now.
            const cartDrawerToRestore = this.cartDrawerToRestore;
            this.cartDrawerToRestore = null;
            this.close();
            this.openSummaryDrawer(cartDrawerToRestore);
          }, 500);
        },
      });
    } catch (exception) {
      this.submit.disabled = false;
      this.error.textContent = this.dataset.updateError || 'Unable to update these items. Please try again.';
    }
  }

  openSummaryDrawer(cartDrawerToRestore) {
    const token = this.transitionToken;
    const open = () => {
      this.summaryOpenTimeoutId = null;
      if (token !== this.transitionToken) return;
      document.querySelector('wishlist-summary-drawer')?.open(this.trigger, { cartDrawerToRestore });
    };
    if (window.innerWidth < 1024) {
      this.summaryOpenTimeoutId = setTimeout(open, 200);
    } else {
      open();
    }
  }

  variantPayload(variant) {
    const productUrl = this.product.url?.startsWith('http') ? this.product.url : `${window.location.origin}${this.product.url}`;
    const image = variant.featured_image?.src || this.product.featured_image || this.product.images?.[0]?.src || this.product.images?.[0];
    return {
      et: 4, empi: Number(this.product.id), epi: Number(variant.id),
      dt: this.product.title, du: productUrl, ct: this.product.type,
      pr: Number(variant.price) / 100, iu: typeof image === 'string' ? image : image?.src,
      vi: this.hasSizeChoice ? this.variantSizeLabel(variant) : '',
    };
  }

  close() {
    this.cancelPendingTransition();
    this.sheet.close();
  }

  // Invalidates any commitChanges() sequence still in flight (network request or the
  // post-success auto-open delay) so closing the panel always cancels it outright.
  cancelPendingTransition() {
    this.transitionToken = (this.transitionToken || 0) + 1;
    if (this.autoOpenTimeoutId) {
      clearTimeout(this.autoOpenTimeoutId);
      this.autoOpenTimeoutId = null;
    }
    if (this.summaryOpenTimeoutId) {
      clearTimeout(this.summaryOpenTimeoutId);
      this.summaryOpenTimeoutId = null;
    }
  }
}

function transitionEnd(element, property, timeoutMs) {
  if (!element) return Promise.resolve();
  return new Promise((resolve) => {
    const finish = () => {
      clearTimeout(timeoutId);
      element.removeEventListener('transitionend', onTransitionEnd);
      resolve();
    };
    const onTransitionEnd = (event) => {
      if (event.target === element && event.propertyName === property) finish();
    };
    const timeoutId = setTimeout(finish, timeoutMs);
    element.addEventListener('transitionend', onTransitionEnd);
  });
}

function productOptionNames(product) {
  return (product.options || []).map((option) => typeof option === 'string' ? option : option.name);
}
if (!customElements.get('collection-wishlist-drawer')) customElements.define('collection-wishlist-drawer', CollectionWishlistDrawer);
